import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Tooltip from 'sushiweb/Tooltip';
import {
  exportCSV,
  getCurrentDate,
  includesOption,
  indexOfOption,
  searchWithName,
} from '../../../helpers';
import DataTables from '../../common/components/DataTables/DataTables';
import {
  creditTransactionsStatusEnum,
  REFRESH_LABEL,
  statusLabels,
} from '../../common/components/constants';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import { giftingProgramRecipientsHeadColumn } from '../../common/components/DataTables/components/headColumns';
import StatusFilter from '../../common/components/StatusFilter';

const GiftingProgramRecipientsTable = ({
  selectedWorkspace,
  programDetails,
  creditTransactions,
  getCreditTransactionForProgram,
  fetchingProgramCreditTransactions,
  fetchedProgramCreditTransactions,
}) => {
  const keyFields = ['email_id', 'created_on'];
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const { programId } = useParams();
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [4],
      label: 'GC_COMPLETED_ENDED',
      disabled: true,
    },
    {
      value: [0, 1, 2, 3, 5, 6, 7, 8, 9],
      label: 'PROCESSING_IN_PROGRESS',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.program_status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getCreditTransactionForProgram({
        workspaceId: selectedWorkspace.workspace_id,
        programId,
      });
  }, [selectedWorkspace]);

  useEffect(() => {
    if (!fetchingProgramCreditTransactions) {
      if (creditTransactions) {
        creditTransactions.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (creditTransactions.length === 0) {
          setDisabledSearch(true);
        }
        setSearchResults(creditTransactions);
        setCurrentData(creditTransactions.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingProgramCreditTransactions]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingProgramCreditTransactions) {
        const arr = searchWithName(creditTransactions, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, fetchingProgramCreditTransactions]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const refreshPage = () => {
    getCreditTransactionForProgram({
      workspaceId: selectedWorkspace.workspace_id,
      programId,
    });
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'email_id', value: 'Email ID' },
      { key: 'created_on', value: 'Created On' },
      { key: 'end_date', value: 'End Date' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = data;
      labelledData.status =
        statusLabels[creditTransactionsStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `${programDetails.name} - Program recipients - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by email ID'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by email ID
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />

        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            isDisabled={disabledSearch}
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          {disabledSearch ? (
            <Download color='grey' style={downloadCSVStyle} />
          ) : (
            <Tooltip
              position='top'
              label='Download the gc program recipients'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
                padding: '10px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedProgramCreditTransactions ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            columns={giftingProgramRecipientsHeadColumn}
            loading={fetchingProgramCreditTransactions || loading}
            data={currentData}
            pagination
            totalRows={searchResults.length}
            handlePageChange={handlePageChange}
            onSort={handleSort}
            emptyStateTitle='No recipients for program'
          />
        )}
      </TableContainer>
    </>
  );
};

GiftingProgramRecipientsTable.propTypes = {
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  creditTransactions: PropTypes.arrayOf(PropTypes.any),
  programDetails: PropTypes.objectOf(PropTypes.any),
  getCreditTransactionForProgram: PropTypes.func,
  fetchingProgramCreditTransactions: PropTypes.bool,
  fetchedProgramCreditTransactions: PropTypes.bool,
};

GiftingProgramRecipientsTable.defaultProps = {
  selectedWorkspace: {},
  creditTransactions: [],
  programDetails: {},
  getCreditTransactionForProgram: _noop,
  fetchingProgramCreditTransactions: false,
  fetchedProgramCreditTransactions: false,
};

export default GiftingProgramRecipientsTable;
